import { Component, Input } from '@angular/core';
import { ContextDataService } from 'src/app/services/context-data/context-data.service';
import { ScopeSequenceSkill } from 'src/app/services/graphql/scope-sequence-skills-response';
import { ScopeSequenceStandard } from 'src/app/services/graphql/scope-sequence-standards-response';
import { LaunchControlService } from 'src/app/services/launch-control/launch-control.service';
import { ContentType, FreckleLinkGeneratorV2Service } from 'src/app/services/link-generators-v2/freckle-link-generator-v2.service';
import { ProductAppCodes } from 'src/app/services/product-info/product-info.service';
import { ClassesService } from 'src/app/services/classes/classes.service';
import { StandardSet } from 'src/app/services/graphql/standard-set-response';
import { GraphqlService } from 'src/app/services/graphql/graphql.service';

export interface FreckleDeepLinkContext {
  rslSkillId: string,
  freckleStandard: string,
  freckleSkillId: string
}

@Component({
  selector: 'app-targeted-practice',
  templateUrl: './targeted-practice.component.html',
  styleUrls: ['./targeted-practice.component.scss']
})
export class TargetedPracticeComponent {
  @Input() public skills: ScopeSequenceSkill[] = [];
  @Input() public standard!: ScopeSequenceStandard;

  public skillsWithDeeplinks: ScopeSequenceSkill[] = [];
  public standardSets: StandardSet[] = [];
  private freckleDeepLinkContexts: FreckleDeepLinkContext[] = [];

  constructor(
    private contextDataService: ContextDataService,
    private freckleLinkGeneratorService: FreckleLinkGeneratorV2Service,
    private classesService: ClassesService,
    private launchControlService: LaunchControlService,
    private graphqlService: GraphqlService
  ) { }

  public async ngOnInit() {
    this.standardSets = await this.classesService.getStandardSetsForClass();
    this.freckleDeepLinkContexts = await this.getFreckleDeeplinkContext();
    this.skillsWithDeeplinks = this.filterSkillsWithNoFreckleDeeplinkContext().sort((a, b) => a.skillName.localeCompare(b.skillName));
  }

  private async getFreckleDeeplinkContext(): Promise<FreckleDeepLinkContext[]> {
    let rslSkillIds = this.skills.map(skill => skill.skillId);
    // TODO: When more than Math is available, remove hardcoded standardset subject.
    let standardSet = this.standardSets.find(s => s.subject === 'math' && s.product === 'freckle');
    if (standardSet) {
      let standards = (await this.graphqlService.getStandardsForSkills(rslSkillIds, standardSet?.standardSetId)).skillMetadata;

      let standardsWithRslIds = standards.filter(standard => standard.rlSkillId);
      return standardsWithRslIds.map(standard => {
        return {
          rslSkillId: standard.rlSkillId!, // safe as we already filtered for standards that have a defined & non-null rlSkillId
          freckleStandard: standard.standardId,
          freckleSkillId: standard.contentActivityId
        };
      });
    }
    return [];
  }

  private filterSkillsWithNoFreckleDeeplinkContext(): ScopeSequenceSkill[] {
    return this.skills.filter(skill => {
      return this.freckleDeepLinkContexts.some(context => context.rslSkillId === skill.skillId);
    });
  }

  public navigateToFreckleSkill(skill: ScopeSequenceSkill): void {

    let students = this.contextDataService.classDataContext$.value?.students || [];

    // "!" is safe as we only pass in skills that we successfuly found a deeplink context for
    const freckleDeepLinkContext = this.freckleDeepLinkContexts.find(context => context.rslSkillId === skill.skillId)!;

    // TODO: When more than Math is available, remove hardcoded ContentType.
    const deeplink = this.freckleLinkGeneratorService.getSearchResultAssignSkillDeepLink(students, freckleDeepLinkContext.freckleStandard, freckleDeepLinkContext.freckleSkillId, ContentType.MathSkills);
    this.launchControlService.launchApp(ProductAppCodes.Freckle, deeplink, true);
  }
}
